import React from 'react';
import { Badge } from './ui/badge';
import { STATUS_LABELS, STATUS_CONFIG } from '../lib/constants';
import { useLanguage } from '../contexts/LanguageContext';

/**
 * Unified package status badge used across the entire app.
 * Renders a cohesive badge with semantic colors, optional dot indicator.
 *
 * @param {string} status - Package status key (e.g. 'in_transit', 'delivered')
 * @param {'default'|'sm'|'lg'} size - Badge size variant
 * @param {boolean} dot - Show pulsing dot indicator (default: true)
 * @param {string} className - Additional CSS classes
 */
const PackageStatusBadge = ({ status, size = 'default', dot = true, className = '' }) => {
  const { language } = useLanguage();
  const labels = STATUS_LABELS[language] || STATUS_LABELS.fr;
  const config = STATUS_CONFIG[status];

  if (!config) {
    return (
      <Badge variant="outline" className={className}>
        {labels[status] || status}
      </Badge>
    );
  }

  const sizeClasses = {
    sm: 'text-[10px] px-1.5 py-0.5 gap-1',
    default: 'text-xs px-2.5 py-1 gap-1.5',
    lg: 'text-sm px-3 py-1.5 gap-2',
  };

  const dotSizes = {
    sm: 'w-1 h-1',
    default: 'w-1.5 h-1.5',
    lg: 'w-2 h-2',
  };

  return (
    <span
      className={`inline-flex items-center font-medium rounded-full border ${config.bg} ${config.text} ${config.border} ${sizeClasses[size]} ${className}`}
    >
      {dot && (
        <span className={`${dotSizes[size]} rounded-full ${config.dot} shrink-0`} />
      )}
      {labels[status] || status}
    </span>
  );
};

export default PackageStatusBadge;
